// DonationEditor (Task 13.2).
//
// Create/edit flow for a Donation. Handles 1-5 image upload, an AI
// classification trigger that displays the suggested category and confidence
// (guidance only), manual fallback when classification is unavailable, and a
// mandatory final-category confirmation before publish (Req 8.1, 9.2, 9.4-9.6,
// 10.1-10.3). The AI suggestion never auto-selects the final category — the
// Restaurant must explicitly confirm it (enforced via the domain
// `finalizeDonationCategory` guard). Field validation mirrors the backend via
// `validateDonationFields`.

import { useState } from "react";
import {
  FOOD_CATEGORIES,
  MAX_IMAGES,
  MIN_IMAGES,
  finalizeDonationCategory,
  shouldInvokeClassifier,
  validateDonationFields,
  type DonationValidationViolation,
  type FoodCategory,
} from "@sharebite/domain";
import { Card } from "../../components/primitives";
import * as api from "../../lib/api";

const VIOLATION_TEXT: Record<DonationValidationViolation, string> = {
  IMAGE_COUNT_OUT_OF_RANGE: "Provide between 1 and 5 images.",
  MEAL_COUNT_NOT_WHOLE_NUMBER: "Meal Count must be a whole number.",
  MEAL_COUNT_OUT_OF_RANGE: "Meal Count must be a positive integer from 1 to 9,999.",
  FOOD_NAME_LENGTH_OUT_OF_RANGE: "Food Name must be 1 to 100 characters.",
  DESCRIPTION_LENGTH_OUT_OF_RANGE: "Description must be at most 1000 characters.",
  SPECIAL_INSTRUCTIONS_LENGTH_OUT_OF_RANGE:
    "Special Instructions must be at most 500 characters.",
  PICKUP_DEADLINE_NOT_AFTER_PREPARATION_TIME:
    "Pickup Deadline must be after the Preparation Time.",
  PICKUP_DEADLINE_OUTSIDE_ALLOWED_WINDOW:
    "Pickup Deadline must be 30 minutes to 3 hours after creation.",
};

type AIState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "suggestion"; category: FoodCategory; confidence: number }
  | { kind: "unavailable" };

function toEpoch(localValue: string): number {
  const ms = new Date(localValue).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export function DonationEditor({
  uid,
  onPublished,
  onCancel,
}: {
  uid: string;
  onPublished?: () => void;
  onCancel?: () => void;
}): JSX.Element {
  const [foodName, setFoodName] = useState("");
  const [description, setDescription] = useState("");
  const [imageRefs, setImageRefs] = useState<string[]>([]);
  const [mealCount, setMealCount] = useState("");
  const [preparationTime, setPreparationTime] = useState("");
  const [pickupDeadline, setPickupDeadline] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [finalCategory, setFinalCategory] = useState<FoodCategory | "">("");

  const [ai, setAi] = useState<AIState>({ kind: "idle" });
  const [aiHint, setAiHint] = useState<string | null>(null);
  const [violations, setViolations] = useState<DonationValidationViolation[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  async function handleAddImages(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const room = MAX_IMAGES - imageRefs.length;
    const toUpload = Array.from(files).slice(0, Math.max(room, 0));
    const uploaded: string[] = [];
    for (const f of toUpload) {
      uploaded.push(await api.uploadDonationImage(uid, f));
    }
    setImageRefs((prev) => [...prev, ...uploaded].slice(0, MAX_IMAGES));
  }

  function removeImage(idx: number): void {
    setImageRefs((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleClassify(): Promise<void> {
    setAiHint(null);
    // The classifier is not invoked without a Food Name and at least one image
    // (Req 9.3).
    if (!shouldInvokeClassifier({ foodName, imageRefs })) {
      setAiHint(
        "Add a Food Name and at least one image before requesting a suggestion.",
      );
      return;
    }
    setAi({ kind: "loading" });
    const result = await api.classifyFood(foodName, imageRefs);
    if (result.ok) {
      // Suggestion is guidance only; it never auto-selects the final category
      // (Req 9.6).
      setAi({
        kind: "suggestion",
        category: result.suggestedCategory,
        confidence: result.confidenceScore,
      });
    } else {
      // Manual fallback on failure (Req 9.4, 10.1, 10.3).
      setAi({ kind: "unavailable" });
    }
  }

  function buildPayload() {
    return {
      foodName,
      description,
      imageRefs,
      mealCount: Number(mealCount),
      preparationTime: toEpoch(preparationTime),
      pickupDeadline: toEpoch(pickupDeadline),
      specialInstructions,
      createdAt: Date.now(),
    };
  }

  async function handlePublish(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setStatus(null);
    // Final category must be explicitly confirmed before publish (Req 9.5).
    if (finalCategory === "") {
      setStatus("Confirm a final food category before publishing.");
      return;
    }
    const payload = buildPayload();
    const result = validateDonationFields(payload);
    if (!result.valid) {
      setViolations(result.violations);
      return;
    }
    setViolations([]);

    // The persisted category is always the Restaurant-confirmed value,
    // regardless of any AI outcome (Req 9.6, 9.7, 10.4).
    const finalized = finalizeDonationCategory({
      confirmedCategory: finalCategory,
      aiOutcome:
        ai.kind === "suggestion"
          ? {
              kind: "suggestion",
              suggestedCategory: ai.category,
              confidenceScore: ai.confidence,
            }
          : { kind: "unavailable" },
      enteredData: payload,
    });

    setBusy(true);
    try {
      const { donationId } = await api.saveDraftDonation({
        foodName: payload.foodName,
        description: payload.description,
        imageRefs: payload.imageRefs,
        mealCount: payload.mealCount,
        foodCategory: finalized.foodCategory,
        preparationTime: payload.preparationTime,
        pickupDeadline: payload.pickupDeadline,
        specialInstructions: payload.specialInstructions,
      });
      await api.publishDonation({ donationId });
      setStatus("Donation published.");
      onPublished?.();
    } catch (err) {
      setStatus(
        err instanceof Error ? err.message : "Publishing failed. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  const hasViolation = (v: DonationValidationViolation) =>
    violations.includes(v);

  return (
    <div className="sb-main">
      <Card title="Create donation">
        <form onSubmit={handlePublish} noValidate>
          <div className="sb-field">
            <label htmlFor="d-food">Food Name</label>
            <input
              id="d-food"
              className="sb-input"
              value={foodName}
              onChange={(e) => setFoodName(e.target.value)}
            />
            {hasViolation("FOOD_NAME_LENGTH_OUT_OF_RANGE") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.FOOD_NAME_LENGTH_OUT_OF_RANGE}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="d-desc">Description</label>
            <textarea
              id="d-desc"
              className="sb-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {hasViolation("DESCRIPTION_LENGTH_OUT_OF_RANGE") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.DESCRIPTION_LENGTH_OUT_OF_RANGE}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="d-images">
              Images ({MIN_IMAGES}-{MAX_IMAGES})
            </label>
            <input
              id="d-images"
              type="file"
              className="sb-input"
              accept=".jpg,.jpeg,.png"
              multiple
              disabled={imageRefs.length >= MAX_IMAGES}
              onChange={(e) => void handleAddImages(e.target.files)}
            />
            <div className="sb-row">
              {imageRefs.map((ref, i) => (
                <span key={ref} className="sb-pill">
                  Image {i + 1}
                  <button
                    type="button"
                    aria-label={`Remove image ${i + 1}`}
                    className="sb-btn sb-btn--secondary"
                    style={{ marginLeft: 6, minHeight: 24, padding: "0 8px" }}
                    onClick={() => removeImage(i)}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
            {hasViolation("IMAGE_COUNT_OUT_OF_RANGE") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.IMAGE_COUNT_OUT_OF_RANGE}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="d-meals">Meal Count (1-9,999)</label>
            <input
              id="d-meals"
              className="sb-input"
              inputMode="numeric"
              value={mealCount}
              onChange={(e) => setMealCount(e.target.value)}
            />
            {hasViolation("MEAL_COUNT_NOT_WHOLE_NUMBER") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.MEAL_COUNT_NOT_WHOLE_NUMBER}
              </span>
            )}
            {hasViolation("MEAL_COUNT_OUT_OF_RANGE") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.MEAL_COUNT_OUT_OF_RANGE}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="d-prep">Preparation Time</label>
            <input
              id="d-prep"
              type="datetime-local"
              className="sb-input"
              value={preparationTime}
              onChange={(e) => setPreparationTime(e.target.value)}
            />
          </div>

          <div className="sb-field">
            <label htmlFor="d-deadline">Pickup Deadline</label>
            <input
              id="d-deadline"
              type="datetime-local"
              className="sb-input"
              value={pickupDeadline}
              onChange={(e) => setPickupDeadline(e.target.value)}
            />
            {hasViolation("PICKUP_DEADLINE_NOT_AFTER_PREPARATION_TIME") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.PICKUP_DEADLINE_NOT_AFTER_PREPARATION_TIME}
              </span>
            )}
            {hasViolation("PICKUP_DEADLINE_OUTSIDE_ALLOWED_WINDOW") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.PICKUP_DEADLINE_OUTSIDE_ALLOWED_WINDOW}
              </span>
            )}
          </div>

          <div className="sb-field">
            <label htmlFor="d-special">Special Instructions</label>
            <textarea
              id="d-special"
              className="sb-textarea"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
            />
            {hasViolation("SPECIAL_INSTRUCTIONS_LENGTH_OUT_OF_RANGE") && (
              <span className="sb-field__error">
                {VIOLATION_TEXT.SPECIAL_INSTRUCTIONS_LENGTH_OUT_OF_RANGE}
              </span>
            )}
          </div>

          {/* AI classification trigger -------------------------------------- */}
          <div className="sb-field">
            <label>AI food category suggestion</label>
            <button
              type="button"
              className="sb-btn sb-btn--secondary"
              onClick={() => void handleClassify()}
              disabled={ai.kind === "loading"}
            >
              {ai.kind === "loading" ? "Classifying..." : "Suggest category (AI)"}
            </button>
            {aiHint && <span className="sb-field__error">{aiHint}</span>}
            {ai.kind === "suggestion" && (
              <p className="sb-muted" data-testid="ai-suggestion">
                Suggested: {ai.category} ({ai.confidence}% confidence). This is a
                suggestion only — confirm your final category below.
              </p>
            )}
            {ai.kind === "unavailable" && (
              <p className="sb-field__error" data-testid="ai-unavailable">
                Automatic classification is unavailable. Please select the food
                category manually.
              </p>
            )}
          </div>

          {/* Mandatory final category --------------------------------------- */}
          <div className="sb-field">
            <label htmlFor="d-final">Final Food Category (required)</label>
            <select
              id="d-final"
              className="sb-select"
              value={finalCategory}
              onChange={(e) => setFinalCategory(e.target.value as FoodCategory)}
            >
              <option value="">Select a category</option>
              {FOOD_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {status && <p className="sb-field__error">{status}</p>}

          <div className="sb-row">
            <button type="submit" className="sb-btn" disabled={busy}>
              {busy ? "Publishing..." : "Publish donation"}
            </button>
            {onCancel && (
              <button
                type="button"
                className="sb-btn sb-btn--secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}
